import React from 'react'
import { ProductImage } from 'app/common/Common'
import {
  DcButton,
  DcCheckbox,
  DcFlexBox,
  DcOverlaySlider,
  DcText,
  PixelCurrency,
} from 'pixel-base'
import styled from 'styled-components'

interface Product {
  product_id: number
  product_name: string
  product_sku: string
  price: number
  stock_quantity: number
  image: string | null
  variation_id: number
  weight: number | null
  tax?: number
}

interface ProductSelectionModalProps {
  isOpen: boolean
  onClose: () => void
  products: Product[]
  scannedSKU: string
  onAddSelected: (selectedProducts: Product[]) => void
}

export const ProductSelectionModal: React.FC<ProductSelectionModalProps> = ({
  isOpen,
  onClose,
  products,
  scannedSKU,
  onAddSelected,
}) => {
  const [selectedProducts, setSelectedProducts] = React.useState<Set<number>>(
    new Set(),
  )

  const handleProductToggle = (productId: number) => {
    const newSelected = new Set(selectedProducts)
    if (newSelected.has(productId)) {
      newSelected.delete(productId)
    } else {
      newSelected.add(productId)
    }
    setSelectedProducts(newSelected)
  }

  const handleSelectAll = () => {
    if (selectedProducts.size === products.length) {
      setSelectedProducts(new Set())
    } else {
      setSelectedProducts(new Set(products.map((p) => p.product_id)))
    }
  }

  const handleAddSelected = () => {
    const selected = products.filter((p) => selectedProducts.has(p.product_id))
    onAddSelected(selected)
    setSelectedProducts(new Set())
    onClose()
  }

  const isAllSelected =
    selectedProducts.size === products.length && products.length > 0

  return (
    <DcOverlaySlider
      title={`Multiple Products Found for "${scannedSKU}"`}
      width="80%"
      isOpen={isOpen}
      onClose={onClose}
    >
      <DcFlexBox flexDirection="column" gap="15px">
        <DcText textSize="14px" style={{ color: '#6c757d' }}>
          Found {products.length} product(s) with SKU &quot;{scannedSKU}&quot;.
          Please select the products you want to add to the order.
        </DcText>

        {/* Select All Checkbox */}
        {products.length > 1 && (
          <DcFlexBox
            style={{
              padding: '10px',
              background: '#f8f9fa',
              borderRadius: '4px',
            }}
          >
            <DcCheckbox
              type="checkbox"
              value="select-all"
              checked={isAllSelected}
              onChange={handleSelectAll}
              label={`Select All (${products.length} products)`}
              name="select-all"
              disabled={false}
            />
          </DcFlexBox>
        )}

        {/* Products List */}
        <ProductsContainer>
          {products.map((product) => (
            <ProductItem key={product.product_id}>
              <DcFlexBox style={{ alignItems: 'center', gap: '10px' }}>
                <DcCheckbox
                  type="checkbox"
                  value={product.product_id.toString()}
                  checked={selectedProducts.has(product.product_id)}
                  onChange={() => handleProductToggle(product.product_id)}
                  name={`product-${product.product_id}`}
                  label=""
                  disabled={false}
                />
                <ProductImage
                  style={{
                    width: '50px',
                    height: '50px',
                    borderRadius: '4px',
                    border: '1px solid #e6e6e6',
                  }}
                  path={product.image}
                />
                <ProductInfo>
                  <DcText
                    textSize="14px"
                    style={{ fontWeight: '600', color: '#495057' }}
                  >
                    {product.product_name}
                  </DcText>
                  <DcText textSize="12px" style={{ color: '#6c757d' }}>
                    SKU: {product.product_sku}
                  </DcText>
                  <DcFlexBox style={{ gap: '20px', marginTop: '4px' }}>
                    <DcText textSize="12px" style={{ color: '#6c757d' }}>
                      Price: <PixelCurrency value={product.price} />
                    </DcText>
                    <DcText textSize="12px" style={{ color: '#6c757d' }}>
                      Stock: {product.stock_quantity}
                    </DcText>
                    {product.weight && (
                      <DcText textSize="12px" style={{ color: '#6c757d' }}>
                        Weight: {product.weight}g
                      </DcText>
                    )}
                  </DcFlexBox>
                </ProductInfo>
              </DcFlexBox>
            </ProductItem>
          ))}
        </ProductsContainer>

        {/* Action Buttons */}
        <DcFlexBox
          gap="10px"
          justifyContent="flex-end"
          style={{ marginTop: '20px' }}
        >
          <DcButton variant="outline-secondary" onClick={onClose} size="sm">
            Cancel
          </DcButton>
          <DcButton
            size="sm"
            onClick={handleAddSelected}
            disabled={selectedProducts.size === 0}
          >
            Add Selected ({selectedProducts.size})
          </DcButton>
        </DcFlexBox>
      </DcFlexBox>
    </DcOverlaySlider>
  )
}

const ProductsContainer = styled.div`
  max-height: 400px;
  overflow-y: auto;
  border: 1px solid #dee2e6;
  border-radius: 4px;
`

const ProductItem = styled.div`
  padding: 15px;
  border-bottom: 1px solid #f1f3f4;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background-color: #f8f9fa;
  }
`

const ProductInfo = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
`

export default ProductSelectionModal
